import {
  PortfolioComparisonView,
  QueryMetadata,
  TechnologyHeatmapView,
  type DuplicateReviewView,
  type QueryResult,
} from '@aurora-pvi/contracts';
import { z } from 'zod';
import { apiFetch } from './apiFetch';
import { filterParams } from './filterParams';
import type { GlobalFilters } from '@aurora-pvi/contracts';

/**
 * The estate screens, from Postgres.
 *
 * The duplicate review was fixture-served and disagreed with every other screen
 * in the product: 34 claims over 1,760 applications in euros, against an estate
 * of 288 claims over 1,900 in dollars, naming applications that exist in no
 * table. The claims were in `core.application_duplicate` the whole time —
 * nothing read them. The portfolio comparison beside it had the same problem
 * one step further on: it compared two fiscal closes nothing had ever frozen.
 */
const ComparisonEnvelope = z.object({ data: PortfolioComparisonView, meta: QueryMetadata });
const HeatmapEnvelope = z.object({ data: TechnologyHeatmapView, meta: QueryMetadata });

export function createHttpEstateRepository(baseUrl: string) {
  return {
    /**
     * What changed between two dates, from the change record itself.
     *
     * The screen was fixture-served and opened on a comparison of two fiscal
     * closes nothing had ever frozen. The dates it offers now are the dates on
     * which the estate demonstrably changed — a record arriving, a record being
     * retired, or a version written against one that stayed — because those are
     * the only three places a structural change leaves a mark.
     */
    async comparePortfolio(
      baselineId?: string,
      comparisonId?: string,
    ): Promise<QueryResult<PortfolioComparisonView>> {
      const params = new URLSearchParams();
      if (baselineId) params.set('baseline', baselineId);
      if (comparisonId) params.set('comparison', comparisonId);
      const response = await apiFetch(`${baseUrl}/portfolio/structure-compare?${params.toString()}`);
      if (!response.ok) {
        throw new Error('The portfolio comparison could not be loaded.');
      }
      return ComparisonEnvelope.parse((await response.json()) as unknown);
    },

    /**
     * Lifecycle against technical debt, and what the placement rules make of
     * each cell.
     *
     * A placement is not a decision, and the two are now shown side by side:
     * `core.application.disposition_code` records what somebody decided, and
     * this records where the model puts the application given its lifecycle and
     * its debt. Where they disagree is the interesting case, and a fixture could
     * not produce one.
     */
    async getTechnologyHeatmap(): Promise<QueryResult<TechnologyHeatmapView>> {
      const response = await apiFetch(`${baseUrl}/applications/heatmap`);
      if (!response.ok) {
        throw new Error('The technology heatmap could not be loaded.');
      }
      return HeatmapEnvelope.parse((await response.json()) as unknown);
    },

    async getDuplicateReview(filters: GlobalFilters): Promise<QueryResult<DuplicateReviewView>> {
      const response = await apiFetch(`${baseUrl}/applications/duplicates?${filterParams(filters)}`);
      if (!response.ok) {
        throw new Error('The duplicate review could not be loaded.');
      }
      return (await response.json()) as { data: DuplicateReviewView; meta: QueryMetadata };
    },

    /**
     * Records a steward's judgement that a pair is not a duplicate.
     *
     * The screen held these in React state until now and said so — *"Local,
     * per-session dismissals. The preview writes nothing."* Which meant every
     * reload asked the same person the same question, and the detector, which
     * skips dismissed pairs, never had one to skip.
     */
    async dismissDuplicate(
      pairId: string,
      dismissal: { reason: string; note?: string },
    ): Promise<{ ok: true } | { ok: false; message: string }> {
      const response = await apiFetch(`${baseUrl}/applications/duplicates/${pairId}/dismiss`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(dismissal),
      });
      if (response.ok) return { ok: true };
      const body = (await response.json().catch(() => ({}))) as {
        message?: string;
        problems?: string[];
      };
      return {
        ok: false,
        message: `${body.message ?? `Request failed (${response.status}).`}${
          body.problems?.length ? ` ${body.problems.join(' ')}` : ''
        }`,
      };
    },
  };
}
